import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import { brand, socialLinks } from "@/mocks/siteData";

const WA_MESSAGE = `Hi Rythugaru! I'd like to know more about your farm experiences and plan a visit. You can reply here on WhatsApp, or reach me on the enquiry line ${brand.phone}.`;

export default function ContactChannels() {
  const whatsappHref = `https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(WA_MESSAGE)}`;

  const channels = [
    {
      icon: "ri-whatsapp-line",
      label: "WhatsApp",
      value: brand.phone,
      note: "Fastest replies · usually under 30 min",
      href: whatsappHref,
      external: true,
      theme: "accent" as const,
    },
    {
      icon: "ri-phone-line",
      label: "Call Us",
      value: brand.phone,
      note: "Every day, 8 AM – 8 PM IST",
      href: `tel:${brand.phone}`,
      external: false,
      theme: "primary" as const,
    },
    {
      icon: "ri-mail-line",
      label: "Email",
      value: brand.email,
      note: "For detailed plans & documents",
      href: `mailto:${brand.email}`,
      external: false,
      theme: "secondary" as const,
    },
    {
      icon: "ri-map-pin-2-line",
      label: "Visit Us",
      value: brand.region,
      note: "Farm visits by appointment",
      href: "/farms",
      external: false,
      theme: "background" as const,
    },
  ];

  const themeClasses: Record<string, string> = {
    accent: "bg-accent-100 text-accent-700",
    primary: "bg-primary-100 text-primary-700",
    secondary: "bg-secondary-100 text-secondary-700",
    background: "bg-background-200 text-foreground-800",
  };

  return (
    <section className="sun-glaze bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              Every Channel
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
            Reach Us the Way That Suits You
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Pick whichever channel is easiest — message, call, email or come say hello in person.
            A real person from the Rythugaru team always answers.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {channels.map((channel, index) => (
            <Reveal key={channel.label} delay={(index % 4) * 80} className="h-full">
              <a
                href={channel.href}
                {...(channel.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                className="h-full flex flex-col bg-background-100 rounded-lg border border-background-200 p-6 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <span
                  className={`w-12 h-12 flex items-center justify-center rounded-full ${themeClasses[channel.theme]}`}
                >
                  <i className={`${channel.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading text-lg text-foreground-950 font-semibold mt-5">
                  {channel.label}
                </h3>
                <p className="text-sm font-medium text-foreground-800 mt-2 break-words">
                  {channel.value}
                </p>
                <p className="text-xs text-foreground-600 leading-relaxed mt-2">{channel.note}</p>
                <span className="flex items-center gap-1 text-sm font-semibold text-accent-600 mt-auto pt-4">
                  Open
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10">
          <div className="flex flex-wrap items-center gap-4 bg-background-100 rounded-lg border border-background-200 px-6 py-5">
            <span className="text-sm font-semibold text-foreground-800">Follow our journey</span>
            <span className="hidden sm:block h-4 w-px bg-background-300"></span>
            <div className="flex items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-800 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}